import { db } from '../../firebase/config'

/**
 * @typedef {object} LoginProfile
 * @property {string|null} id - Het ID-veld uit het Users-document
 * @property {string[]|null} organisations - De CompagnyID's waar dit account bij hoort
 * @property {boolean|null} verified - Of het account is goedgekeurd
 * @property {boolean} profileMissing - true wanneer er geen Users-document is
 */

/**
 * Haalt op waar het inlogscherm iemand heen moet sturen: het ID, de
 * organisaties en of het account goedgekeurd is.
 *
 * Leest rechtstreeks uit Firestore en niet via de `userInfoLogin` Cloud
 * Function. Die deed exact dezelfde read, maar draaide op generatie 1 met
 * 256 MB en laadde bij een koude start de hele `functions/index.js` — in de
 * productielogs 3,3 seconde, tegenover 66 ms warm. Dat wachten zit tussen het
 * invoeren van het wachtwoord en het dashboard. De rechten staan de read toe
 * (`Users` heeft `allow read: if isAuth()`, nagelopen tegen de live ruleset) en
 * `Auth.js` doet vlak daarna al eenzelfde directe read.
 * Zie docs/inbox.md Bug #221.
 *
 * Gooit wanneer het lezen mislukt. Dat is met opzet geen `profileMissing`: een
 * storing van tien seconden is iets anders dan een account zonder organisatie,
 * en toen die twee hetzelfde antwoord gaven logde het inlogscherm mensen uit
 * met een melding die niet klopte. Zie docs/inbox.md Bug #137.
 *
 * @param {string} uid - De Firebase Auth-uid; Users-documenten staan op Users/{uid}
 * @returns {Promise<LoginProfile>}
 */
const fetchLoginProfile = async (uid) => {
    // Sluit dicht in plaats van open: zonder uid is er niets te lezen, en een
    // read op .doc(undefined) zou gooien met een melding die nergens naar wijst.
    // AI-CONTEXT: See docs/pitfalls.md#state-gate-defaults-open-while-loading
    if (!uid) throw new Error('fetchLoginProfile: geen uid meegegeven')

    const snapshot = await db.collection('Users').doc(uid).get()

    if (!snapshot.exists) {
        return { id: null, organisations: null, verified: null, profileMissing: true }
    }

    const profile = snapshot.data()

    // Alles expliciet naar null: het inlogscherm toetst hierop, en `undefined`
    // laat een ontbrekend veld er hetzelfde uitzien als een veld dat er wel is.
    return {
        id: profile.ID || null,
        organisations: profile.Compagny || null,
        verified: profile.Approved ?? null,
        profileMissing: false,
    }
}

export default fetchLoginProfile
